/**
 * PreviewModal — etapa entre "colar URLs" e "criar jobs".
 *
 * Mostra:
 *  - URLs single video (1 checkbox cada)
 *  - Blocos expandidos (perfil IG, playlist YT) com header + checkboxes
 *  - Avisos: ja transcritos (com link), ja em fila, ignorados
 *  - Contador de selecionados + botao "Adicionar à fila"
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, Check, AlertCircle, Loader2, Youtube, Instagram, PlayCircle, Globe,
} from 'lucide-react';

type FonteVideo = 'youtube' | 'instagram' | 'panda_video' | 'generic';

interface PreviewItem {
  url: string;
  urlNorm: string;
  fonte: FonteVideo;
  titulo?: string;
  thumbnail?: string;
  duracao?: number;
  publicadoEm?: string;
  expandidoDe?: string;
}

interface PreviewBloco {
  urlOriginal: string;
  tipo: 'youtube_playlist' | 'youtube_channel' | 'instagram_profile' | 'instagram_reels_list';
  total: number;
  truncado: boolean;
  items: PreviewItem[];
  erro?: string;
}

export interface PreviewResultado {
  items: PreviewItem[];
  expansoes: PreviewBloco[];
  jaTranscritos: { url: string; transcricaoId: string; titulo: string | null; criadoEm: string }[];
  jaEmFila: { url: string; jobId: string; status: string }[];
  ignorados: { url: string; motivo: string }[];
}

const FONTE_ICON: Record<FonteVideo, typeof Youtube> = {
  youtube: Youtube,
  instagram: Instagram,
  panda_video: PlayCircle,
  generic: Globe,
};

const TIPO_LABEL: Record<PreviewBloco['tipo'], string> = {
  youtube_playlist: 'Playlist YouTube',
  youtube_channel: 'Canal YouTube',
  instagram_profile: 'Perfil Instagram',
  instagram_reels_list: 'Reels do perfil Instagram',
};

function formatarDuracao(seg?: number): string {
  if (!seg) return '';
  const m = Math.floor(seg / 60);
  const s = Math.floor(seg % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatarData(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR');
}

interface Props {
  preview: PreviewResultado;
  loading: boolean;
  onCancelar: () => void;
  onConfirmar: (urls: string[]) => Promise<void>;
}

export default function PreviewModal({ preview, loading, onCancelar, onConfirmar }: Props) {
  // urlNorm -> selecionado
  const allUrls = useMemo(() => {
    const urls: string[] = [];
    for (const i of preview.items) urls.push(i.urlNorm);
    for (const b of preview.expansoes) for (const i of b.items) urls.push(i.urlNorm);
    return urls;
  }, [preview]);

  const [sel, setSel] = useState<Set<string>>(() => new Set(allUrls));
  const [enviando, setEnviando] = useState(false);

  // Re-sync quando preview muda (todos selecionados por default)
  useEffect(() => { setSel(new Set(allUrls)); }, [allUrls]);

  const toggle = (urlNorm: string) => {
    setSel((s) => {
      const novo = new Set(s);
      if (novo.has(urlNorm)) novo.delete(urlNorm);
      else novo.add(urlNorm);
      return novo;
    });
  };

  const marcarTodos = () => setSel(new Set(allUrls));
  const desmarcarTodos = () => setSel(new Set());

  const totalDisponivel = allUrls.length;
  const totalSelecionado = sel.size;

  async function confirmar() {
    if (!totalSelecionado || enviando) return;
    setEnviando(true);
    try {
      // Busca url original (nao normalizada) pra cada urlNorm selecionado
      const urls: string[] = [];
      for (const i of preview.items) {
        if (sel.has(i.urlNorm)) urls.push(i.url);
      }
      for (const b of preview.expansoes) {
        for (const i of b.items) {
          if (sel.has(i.urlNorm)) urls.push(i.url);
        }
      }
      await onConfirmar(urls);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Pré-visualizar antes de adicionar</h2>
          <button onClick={onCancelar} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Buscando metadados das URLs...
            </div>
          ) : (
            <>
              {/* Avisos */}
              {preview.jaTranscritos.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3 text-sm">
                  <p className="font-medium text-yellow-800 dark:text-yellow-200 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {preview.jaTranscritos.length} já transcrita{preview.jaTranscritos.length === 1 ? '' : 's'} antes — pulada{preview.jaTranscritos.length === 1 ? '' : 's'}
                  </p>
                  <ul className="mt-1 space-y-0.5 ml-5 list-disc text-xs text-yellow-700 dark:text-yellow-400">
                    {preview.jaTranscritos.map((t) => (
                      <li key={t.transcricaoId}>
                        <Link to={`/transcricoes/${t.transcricaoId}`} className="text-yellow-700 dark:text-yellow-400 underline hover:text-yellow-900">
                          {t.titulo || t.url}
                        </Link>
                        <span className="ml-1 text-yellow-600">({formatarData(t.criadoEm)})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.jaEmFila.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3 text-sm">
                  <p className="font-medium text-blue-800 dark:text-blue-200 flex items-center gap-1">
                    <Loader2 className="w-4 h-4" /> {preview.jaEmFila.length} já em fila — pulada{preview.jaEmFila.length === 1 ? '' : 's'}
                  </p>
                  <ul className="mt-1 space-y-0.5 ml-5 list-disc text-xs text-blue-700 dark:text-blue-300">
                    {preview.jaEmFila.map((j) => (
                      <li key={j.jobId}>
                        <span className="font-mono">{j.url}</span> <span className="text-blue-600">({j.status})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.ignorados.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-sm">
                  <p className="font-medium text-red-800 dark:text-red-200 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {preview.ignorados.length} ignorada{preview.ignorados.length === 1 ? '' : 's'}
                  </p>
                  <ul className="mt-1 space-y-0.5 ml-5 list-disc text-xs text-red-700 dark:text-red-400">
                    {preview.ignorados.map((i, idx) => (
                      <li key={idx} className="break-all">
                        <span className="font-mono">{i.url}</span> — {i.motivo}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Single videos */}
              {preview.items.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Vídeos individuais
                  </h3>
                  <div className="space-y-1">
                    {preview.items.map((it) => (
                      <ItemLinha key={it.urlNorm} item={it} selecionado={sel.has(it.urlNorm)} onToggle={() => toggle(it.urlNorm)} />
                    ))}
                  </div>
                </section>
              )}

              {/* Blocos de expansao */}
              {preview.expansoes.map((bloco) => (
                <section key={bloco.urlOriginal} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                  <header className="flex items-center justify-between mb-2">
                    <div className="text-xs">
                      <span className="text-gray-500 dark:text-gray-400">{TIPO_LABEL[bloco.tipo]}:</span>{' '}
                      <span className="font-mono text-gray-700 dark:text-gray-300 break-all">{bloco.urlOriginal}</span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {bloco.items.length} de {bloco.total}
                      {bloco.truncado && ' (cap. 20)'}
                    </span>
                  </header>
                  {bloco.erro ? (
                    <p className="text-sm text-red-700 dark:text-red-400">{bloco.erro}</p>
                  ) : bloco.items.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">Tudo já foi transcrito antes — nada a adicionar.</p>
                  ) : (
                    <div className="space-y-1">
                      {bloco.items.map((it) => (
                        <ItemLinha key={it.urlNorm} item={it} selecionado={sel.has(it.urlNorm)} onToggle={() => toggle(it.urlNorm)} />
                      ))}
                    </div>
                  )}
                </section>
              ))}

              {totalDisponivel === 0 && (
                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nada para adicionar.
                </div>
              )}
            </>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
            {totalDisponivel > 0 && (
              <>
                <button onClick={marcarTodos} className="text-purple-600 hover:underline">
                  marcar todos
                </button>
                <button onClick={desmarcarTodos} className="text-purple-600 hover:underline">
                  desmarcar todos
                </button>
                <span className="text-gray-500 dark:text-gray-400">•</span>
                <span>
                  <strong>{totalSelecionado}</strong> de {totalDisponivel} selecionados
                </span>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onCancelar}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900"
            >
              Cancelar
            </button>
            <button
              onClick={confirmar}
              disabled={!totalSelecionado || enviando}
              className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50 flex items-center gap-1"
            >
              {enviando ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adicionando...</>
              ) : (
                <><Check className="w-3.5 h-3.5" /> Adicionar {totalSelecionado || ''} à fila</>
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ItemLinha({ item, selecionado, onToggle }: { item: PreviewItem; selecionado: boolean; onToggle: () => void }) {
  const Icon = FONTE_ICON[item.fonte];
  return (
    <label className="flex items-start gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 p-1 rounded cursor-pointer">
      <input
        type="checkbox"
        checked={selecionado}
        onChange={onToggle}
        className="mt-1 shrink-0"
      />
      {item.thumbnail ? (
        <img src={item.thumbnail} alt="" className="w-16 h-9 object-cover rounded bg-gray-100 dark:bg-gray-700 shrink-0" loading="lazy" />
      ) : (
        <div className="w-16 h-9 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900 dark:text-gray-100 line-clamp-1">
          {item.titulo || item.url}
        </p>
        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <Icon className="w-3 h-3" />
          {item.duracao && <span>{formatarDuracao(item.duracao)}</span>}
          {item.publicadoEm && <><span>·</span><span>{formatarData(item.publicadoEm)}</span></>}
        </div>
      </div>
    </label>
  );
}
